/**
 * Which team to put back into an EMPTY selection from the session.
 *
 * Fill-only, as develop has it. The session never REPLACES a team the context
 * is holding: forward navigation writes the context directly (the start step
 * hands its commit over on Next — see `committedSelection`), and a team rep's
 * pick on the register step's dropdown is theirs until they submit it. Every
 * attempt to let the session overwrite a held team broke one of those two —
 * either the dropdown snapped back to the committed team, or a stale team
 * survived a change made on the start step.
 *
 * What is left for this to do is the blank case: a reload, or a rep arriving at
 * the register step with no team held, whose earlier choice the session still
 * records.
 */

import { findDivisionTeamById } from '@sportninja/common/features/registration/startStep/divisionTeamIndex'
import type {
  StartRole,
  StartTeam,
} from '@sportninja/common/features/registration/startStep/types'

interface RepTeam {
  uid?: string | null
}

export const sessionTeamToRestore = <R extends RepTeam>({
  sessionTeamId,
  hasSelection,
  role,
  userTeams,
  registrationTeams,
}: {
  sessionTeamId: string | null | undefined
  /** A team is already held — never replaced. */
  hasSelection: boolean
  /** Null until the role can be placed; see `startRoleFor`. */
  role: StartRole | null
  /** A rep's OWN teams. Not fetched for players. */
  userTeams: R[] | null | undefined
  /** Every team in the competition, from the registration payload. */
  registrationTeams: Parameters<typeof findDivisionTeamById>[0]
}): R | StartTeam | null => {
  if (!sessionTeamId || hasSelection || !role) {
    return null
  }

  if (role === 'team') {
    // A rep's team comes from their OWN teams and nowhere else. Their session
    // can still carry a PLAYER's team id after switching roles on the start
    // step — `updateSessionStart` omits `team_id` for a rep rather than
    // clearing it — and that team must not appear in the rep's dropdown. It
    // would also be the wrong shape there: the registration payload's team
    // lacks the roster row the dropdown matches options on.
    return userTeams?.find((team) => team?.uid === sessionTeamId) ?? null
  }

  // A player chose their team on the start step, from the whole competition —
  // the new flow, and the reason this is not develop's `userTeams`-only lookup.
  // `userTeams` is not fetched for players any more.
  return findDivisionTeamById(registrationTeams, sessionTeamId)?.team ?? null
}
